/**
 * Workspace controller store — Zustand store that owns workspace-shape state.
 *
 * One controller exists per scope (dashboard, history). It owns:
 *   - Tree of layouts (`treeItems` + `treeOpenState`)
 *   - Active layout ID
 *   - Workspace revision (for shape concurrency)
 *   - Connection status
 *   - Bootstrap promise (consumed by per-layout content store)
 *
 * It does NOT own active layout content (mosaic tree, panels, configs) —
 * that lives in the per-layout content store (`workspace-store.ts`),
 * which is destroyed and recreated on layout switch. Chart assets
 * (templates, drawings) are chart-owned — see
 * `features/charts/chart-assets-store.ts`.
 *
 * State shape is phase-discriminated:
 *
 *   - `data: WorkspaceData` — the always-present payload (tree, active
 *     layout, etc.). Hydrated from IDB at boot; updated by mutations and
 *     broadcasts.
 *
 *   - `phase: WorkspacePhase` — `Loading` (pre-bootstrap), `Loaded`
 *     (server-confirmed, carries `workspaceRevision`), or `Stale`
 *     (mutation failed irrecoverably, carries `message`).
 *
 *   - `connectionStatus: ConnectionStatus` — reachability, orthogonal to
 *     lifecycle phase.
 *
 *   - `bootstrapPromise` — Promise resource exposed for the per-layout
 *     content store to await. Top-level (not part of data or phase).
 */

import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { createStore } from 'zustand/vanilla'
import { devtools } from 'zustand/middleware'
import type { StoreApi } from 'zustand'
import { getWorkspaceDoc, setWorkspaceDoc } from '../idb-cache'
import { registerPendingCreate } from '../pending-creates'
import type { BootstrapData, MutateTreeOp, WindowConfig, PanelState } from '../types'
import type { GridCell, LayoutMode, TabGroup, WorkspaceDocument } from '@/types/schemas/workspace'
import {
  ROOT_ID,
  type TreeItems,
  createNode,
  deleteNodes as deleteNodesPure,
  duplicateSubtree,
  findParent,
  moveNodes as moveNodesPure,
  pickNextActive,
  renameNode as renameNodePure,
} from '../tree-state-manager'
import { hydrateFromCache, makeBootstrapQueue, CACHE_HYDRATE_TIMEOUT_MS } from './bootstrap'
import { runMutation, type OptimisticResult } from './mutations'
import { openBroadcast, type BroadcastBinding } from './broadcast'
import { attachRevisionSync, type RevisionSyncBinding } from './revision-sync'
import { SerialQueue } from '../serial-queue'
import type { ConnectionStatus, WorkspaceData, WorkspacePhase } from './state'

// ─── Re-exports for back-compat with the index barrel ───
export type { ConnectionStatus, WorkspaceData, WorkspacePhase } from './state'
export type { LoadingPhase, LoadedPhase, StalePhase } from './state'

// ─── Layout content payload (used by createLayout) ───

export interface LayoutContent {
  mosaicTree: unknown
  windowConfigs: Record<string, WindowConfig>
  panels: PanelState[]
  globalSettings?: Record<string, unknown>
  tabGroups?: Record<string, TabGroup>
  /** Grid (scroll) layouts: structure lives in gridLayout, mosaicTree is null. */
  layoutMode?: LayoutMode
  gridLayout?: GridCell[]
}

function panelsArrayToRecord(panels: PanelState[]): Record<string, PanelState> {
  const record: Record<string, PanelState> = {}
  for (const p of panels) {
    const id = (p as Record<string, unknown>)?.panelId as string | undefined
    if (id) record[id] = p
  }
  return record
}

// ─── State shape ───

export interface WorkspaceStoreState {
  /**
   * Workspace scope (`"dashboard"`, `"history"`, etc.). Stable for the
   * lifetime of the store; mirrored from `config.scope` at construction so
   * consumers (chart-panel, profile API) can read it via the standard
   * `useWorkspaceStore` selector without prop-drilling.
   */
  scope: string

  /**
   * Always-present payload. Hydrated from IDB at construction; updated by
   * mutations and broadcasts. Doesn't go away when the controller goes
   * Stale — the user still sees the tree they were working with.
   */
  data: WorkspaceData

  /**
   * Lifecycle tag. `Loading` until the first bootstrap resolves, then
   * `Loaded` (carrying `workspaceRevision`). Transitions to `Stale`
   * (carrying `message`) when a server call fails non-recoverably.
   */
  phase: WorkspacePhase

  /** Reachability of the server. Orthogonal to phase. */
  connectionStatus: ConnectionStatus

  /**
   * Bootstrap promise — consumed by content store on initial mount.
   *
   * Resolves to null when the bootstrap queue's task-entry `shouldStillRun`
   * check skips a queued bootstrap (e.g. inbound broadcast arrived but a
   * prior task already brought local state current). The init path never
   * passes `shouldStillRun`, so the FIRST bootstrap always resolves with
   * data — content-store consumers can rely on that.
   */
  bootstrapPromise: Promise<BootstrapData | null> | null
}

export interface WorkspaceStoreActions {
  // Lifecycle (called by Provider)
  _init: () => void
  _destroy: () => void

  // Shape mutations — all route through POST /mutate. Optimistic state
  // applies synchronously; server call + broadcast + IDB happen in the
  // mutation queue (see mutations.ts). Actions return the optimistic
  // client-generated ID where applicable so callers can switch to it
  // immediately. If the server later rejects, the controller goes stale
  // and prompts a refresh.
  createLayout: (
    name: string,
    content: LayoutContent,
    parentId?: string,
    index?: number,
  ) => string | null
  createFolder: (name: string, parentId?: string, index?: number) => string | null
  deleteNodes: (nodeIds: string[]) => void
  renameNode: (nodeId: string, name: string) => void
  moveNodes: (nodeIds: string[], targetParentId: string, index: number) => void
  duplicateNode: (nodeId: string) => string | null
  setActiveLayout: (layoutId: string) => void
  setOpenState: (openState: string[]) => void
  restoreLayout: (layoutId: string) => void
}

export type WorkspaceStoreInternalState = WorkspaceStoreState & WorkspaceStoreActions

export type WorkspaceStore = StoreApi<WorkspaceStoreInternalState>

// ─── Config ───

export interface WorkspaceStoreConfig {
  scope: string
}

function defaultTree(): TreeItems {
  return {
    [ROOT_ID]: { id: ROOT_ID, name: 'root', isFolder: true, children: [] },
  }
}

function emptyData(): WorkspaceData {
  return {
    treeItems: defaultTree(),
    treeOpenState: [],
    activeLayoutId: null,
    newItemIds: new Set<string>(),
    needsOnboarding: false,
  }
}

// ─── Factory ───

export function createWorkspaceStore(config: WorkspaceStoreConfig): {
  store: WorkspaceStore
  destroy: () => void
} {
  let broadcastBinding: BroadcastBinding | null = null
  let syncBinding: RevisionSyncBinding | null = null
  let bootstrapPromise: Promise<BootstrapData | null> | null = null
  // FIFO queue for server-bound mutations. The queue serializes requests
  // so they land in order; broadcast + IDB writes only fire in onSuccess
  // (after the server has committed). On any failure the queue clears and
  // the controller goes stale. See serial-queue.ts.
  const persistenceQueue = new SerialQueue()
  // Serializes runBootstrap calls — init + inbound broadcasts go through
  // this so they can never race-write IDB. See makeBootstrapQueue.
  const { enqueueBootstrap } = makeBootstrapQueue()

  const store = createStore<WorkspaceStoreInternalState>()(
    devtools(
      (set, get) => {
        const ctx = () => ({
          scope: config.scope,
          store,
          broadcast: broadcastBinding,
          persistenceQueue,
        })

        return {
          // ─── Initial state ───
          scope: config.scope,
          data: emptyData(),
          phase: { kind: 'Loading', sub: 'hydrating-cache' } as WorkspacePhase,
          connectionStatus: 'connecting' as ConnectionStatus,
          bootstrapPromise: null,

          // ─── Lifecycle ───
          //
          // _init / _destroy are idempotent and re-runnable — required for
          // React StrictMode (mount → cleanup → remount in dev) and for HMR
          // (module reload tears down + re-establishes the Provider effect).
          // _init only creates bindings that are currently null; _destroy
          // closes and nulls them. Calling _init twice without an intervening
          // _destroy is a no-op.
          _init: () => {
            if (!broadcastBinding) {
              broadcastBinding = openBroadcast({
                scope: config.scope,
                store,
                isPersistenceQueueBusy: () => persistenceQueue.isBusy(),
                enqueueBootstrap,
              })
            }
            if (!syncBinding) {
              syncBinding = attachRevisionSync({
                enqueueBootstrap,
                scope: config.scope,
                store,
                isBusy: () => persistenceQueue.isBusy(),
              })
            }

            // Stage 1: hydrate from IDB synchronously (instant render).
            // Stage 2: fetch bootstrap, reconcile IDB, apply to the store.
            // Only kick off once — subsequent _init calls (after cleanup +
            // remount) reuse the original promise so we don't re-fetch.
            //
            // The init enqueue does NOT pass shouldStillRun, so it always
            // runs (never no-ops). bootstrapPromise's `null` branch only
            // fires for skipped inbound-broadcast bootstraps.
            if (!bootstrapPromise) {
              bootstrapPromise = (async () => {
                // Race the hydrate against a deadline: a hung indexedDB.open
                // (Safari bfcache bug) must not gate the network fetch. If the
                // hydrate settles late, its setState is a guarded no-op once
                // the phase has left Loading (see hydrateFromCache).
                let hydrateDeadline: ReturnType<typeof setTimeout> | undefined
                await Promise.race([
                  hydrateFromCache({ scope: config.scope, store }),
                  new Promise<void>((resolve) => {
                    hydrateDeadline = setTimeout(resolve, CACHE_HYDRATE_TIMEOUT_MS)
                  }),
                ])
                clearTimeout(hydrateDeadline)
                return enqueueBootstrap({
                  scope: config.scope,
                  store,
                })
              })()
              set({ bootstrapPromise }, undefined, 'controller/init')
              bootstrapPromise.catch(() => {})
            }
          },

          _destroy: () => {
            broadcastBinding?.close()
            syncBinding?.close()
            broadcastBinding = null
            syncBinding = null
            // Drop any pending mutations queued behind the in-flight task.
            // The in-flight task itself can't be cancelled — it'll complete
            // and write IDB for this scope (which is harmless: the right
            // key, server-confirmed data) — but any tasks queued after it
            // shouldn't fire post-navigation. This keeps parity with the
            // workspace-store's destroy, which clears its save queue too.
            persistenceQueue.clear()
            // Note: bootstrapPromise stays — it's data, not a resource to
            // free. A subsequent _init will reuse it.
          },

          // ─── createLayout ───
          createLayout: (name, content, parentId = ROOT_ID, index) => {
            let optimisticId = ''
            let resolvedName = name
            const panelsRecord = panelsArrayToRecord(content.panels)

            // Settled when the create mutation reaches a final outcome.
            // Registered below so the new layout's content store defers its
            // first server calls until the layout exists server-side (the
            // two persistence queues are otherwise unordered).
            let settleCreate: () => void = () => {}
            const createSettled = new Promise<void>((resolve) => {
              settleCreate = resolve
            })

            runMutation(
              ctx(),
              [],
              (items): OptimisticResult => {
                optimisticId = createNode(items, parentId, name, false, index)
                resolvedName = items[optimisticId].name
                return {
                  items,
                  activeLayoutId: optimisticId,
                  createdIds: [optimisticId],
                }
              },
              (id) => [
                {
                  op: 'create',
                  id,
                  parentId,
                  index,
                  node: { name: resolvedName, isFolder: false },
                  layoutData: {
                    name: resolvedName,
                    mosaicTree: content.mosaicTree,
                    windowConfigs: content.windowConfigs,
                    panels: panelsRecord,
                    globalSettings: content.globalSettings,
                    ...(content.tabGroups ? { tabGroups: content.tabGroups } : {}),
                    ...(content.layoutMode === 'grid'
                      ? { layoutMode: 'grid' as const, gridLayout: content.gridLayout ?? [] }
                      : {}),
                  },
                },
                { op: 'set_active', layoutId: id },
              ],
              () => settleCreate(),
            )

            if (optimisticId) {
              trackEvent(EVENTS.LAYOUT_CREATED, {
                layout_id: optimisticId,
                workspace_id: config.scope,
              })
              registerPendingCreate([optimisticId], createSettled)
              set((s) => ({ data: { ...s.data, needsOnboarding: false } }))
              // Seed the new layout's content into IDB NOW, before the optimistic
              // activeLayoutId flip remounts the layout-store keyed by this id. That
              // store hydrates from getWorkspaceDoc on mount (init stage 1) — without
              // this write it finds nothing, races the still-queued create over the
              // network, and renders the static default panel instead of the preset.
              // Must be the optimistic path, not the mutation onSuccess: the remount
              // happens before the server responds.
              const doc: WorkspaceDocument = {
                version: 2,
                name: resolvedName,
                mosaicTree: content.mosaicTree as WorkspaceDocument['mosaicTree'],
                windowConfigs: content.windowConfigs,
                globalSettings: content.globalSettings ?? {},
                panels: panelsRecord,
                // The server assigns every created layout revision 1
                // (workspace_tree_service.on_create_leaf). Stamp it so the
                // layout-store mounts already knowing its revision. Without
                // it, the store's loadLayout fetch can race the queued create
                // (GET returns null), leaving the revision unknown and the
                // first structural save either deferred or rejected with 409
                // ("workspace is out of sync").
                layoutRevision: 1,
                ...(content.tabGroups ? { tabGroups: content.tabGroups } : {}),
                // Grid (scroll) layouts: without these the remount hydrates the
                // imported/created layout as mosaic (empty tree = blank).
                ...(content.layoutMode === 'grid'
                  ? { layoutMode: 'grid' as const, gridLayout: content.gridLayout ?? [] }
                  : {}),
              }
              void setWorkspaceDoc(config.scope, optimisticId, doc)
            }
            return optimisticId || null
          },

          // ─── createFolder ───
          createFolder: (name, parentId = ROOT_ID, index) => {
            let optimisticId = ''
            runMutation(
              ctx(),
              [],
              (items): OptimisticResult => {
                optimisticId = createNode(items, parentId, name, true, index)
                return { items, createdIds: [optimisticId] }
              },
              (id) => [{ op: 'create', id, parentId, index, node: { name, isFolder: true } }],
            )
            return optimisticId || null
          },

          // ─── deleteNodes ───
          deleteNodes: (nodeIds) => {
            const ops: MutateTreeOp[] = [{ op: 'delete', nodeIds }]
            const currentActive = get().data.activeLayoutId

            runMutation(ctx(), ops, (items): OptimisticResult => {
              const removedLayoutIds = deleteNodesPure(items, nodeIds)
              let nextActive: string | null | undefined
              if (currentActive && removedLayoutIds.includes(currentActive)) {
                nextActive = pickNextActive(items)
                if (nextActive) {
                  ops.push({ op: 'set_active', layoutId: nextActive })
                }
              }
              return {
                items,
                ...(nextActive !== undefined ? { activeLayoutId: nextActive } : {}),
              }
            })
          },

          // ─── renameNode ───
          renameNode: (nodeId, name) => {
            runMutation(ctx(), [{ op: 'rename', nodeId, name }], (items): OptimisticResult => {
              renameNodePure(items, nodeId, name)
              return { items }
            })
          },

          // ─── moveNodes ───
          moveNodes: (nodeIds, targetParentId, index) => {
            runMutation(
              ctx(),
              [{ op: 'move', nodeIds, parentId: targetParentId, index }],
              (items): OptimisticResult => {
                moveNodesPure(items, nodeIds, targetParentId, index)
                return { items }
              },
            )
          },

          // ─── duplicateNode ───
          duplicateNode: (nodeId) => {
            let optimisticId = ''
            let clientIdMap: Record<string, string> = {}
            // The copy's name travels with the op: the server names the root of the copied subtree
            // with it (tree item and layout document), so the optimistic name survives a reload.
            let copyName = ''

            // Same deal as createLayout: the duplicated layouts must not be
            // saved/loaded by their content stores before the duplicate op
            // commits server-side.
            let settleCreate: () => void = () => {}
            const createSettled = new Promise<void>((resolve) => {
              settleCreate = resolve
            })

            runMutation(
              ctx(),
              [],
              (items): OptimisticResult => {
                const { idMap, newItems } = duplicateSubtree(items, nodeId)
                clientIdMap = idMap
                copyName = newItems[idMap[nodeId]]?.name ?? ''
                Object.assign(items, newItems)
                const parentId = findParent(items, nodeId)
                if (parentId && items[parentId]) {
                  const children = items[parentId].children
                  const idx = children.indexOf(nodeId)
                  const newRootId = idMap[nodeId]
                  children.splice(idx + 1, 0, newRootId)
                  optimisticId = newRootId
                }
                return { items, createdIds: [optimisticId] }
              },
              () => [{ op: 'duplicate', nodeId, idMap: clientIdMap, name: copyName }],
              () => settleCreate(),
            )

            if (optimisticId) {
              const items = get().data.treeItems
              const newLeafIds = Object.values(clientIdMap).filter(
                (id) => items[id] && !items[id].isFolder,
              )
              registerPendingCreate(newLeafIds, createSettled)
              // Seed each duplicated leaf's content into IDB from its
              // source's cached doc so a fast switch to the duplicate
              // hydrates instantly and knows its revision. The server
              // assigns duplicated layouts revision 1
              // (workspace_tree_service.on_duplicate_leaf), NOT the
              // source's revision. Sources not cached locally are skipped;
              // the layout store's loadLayout fetch (gated on the pending
              // create above) covers those.
              for (const [oldId, newId] of Object.entries(clientIdMap)) {
                const item = items[newId]
                if (!item || item.isFolder) continue
                void getWorkspaceDoc(config.scope, oldId).then((doc) => {
                  if (!doc) return
                  void setWorkspaceDoc(config.scope, newId, {
                    ...doc,
                    name: item.name,
                    layoutRevision: 1,
                  })
                })
              }
            }
            return optimisticId || null
          },

          // ─── setActiveLayout ───
          setActiveLayout: (layoutId) => {
            // Every caller is a user switch (header dropdown, import); the
            // restore path sets activeLayoutId directly in bootstrap and
            // never comes through here.
            trackEvent(EVENTS.LAYOUT_SWITCHED, { layout_id: layoutId, workspace_id: config.scope })
            // Clear new-item indicator on activation
            set((s) => {
              if (!s.data.newItemIds.has(layoutId)) return s
              const next = new Set(s.data.newItemIds)
              next.delete(layoutId)
              return { data: { ...s.data, newItemIds: next } }
            })

            runMutation(
              ctx(),
              [{ op: 'set_active', layoutId }],
              (items): OptimisticResult => ({
                items,
                activeLayoutId: layoutId,
              }),
            )
          },

          // ─── setOpenState ───
          setOpenState: (openState) => {
            runMutation(
              ctx(),
              [{ op: 'set_open_state', openState }],
              (items): OptimisticResult => ({ items, openState }),
            )
          },

          // ─── restoreLayout ───
          restoreLayout: (layoutId) => {
            runMutation(
              ctx(),
              [{ op: 'restore', nodeId: layoutId }],
              (items): OptimisticResult => ({ items }),
            )
          },
        }
      },
      { name: `workspace-controller:${config.scope}` },
    ),
  )

  return {
    store,
    destroy: () => {
      store.getState()._destroy()
    },
  }
}
